"use client";

import {
  useCallback,
  useRef,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from "react";
import type { ParsedAddress } from "@/components/inputs/address-types";
import {
  formatDeliveryLocation,
  isValidUsZip,
  resolveManualDeliveryLocation,
  type DeliveryLocation,
} from "@/lib/checkout/delivery-location";
import type { SavedAddressOption } from "@/lib/checkout/saved-address-options";
import type { useSaveDeliveryAddress } from "./use-save-delivery-address";

type AddressBook = ReturnType<typeof useSaveDeliveryAddress>;

interface DeliveryAddressInputSetters {
  setChosenLocation: Dispatch<SetStateAction<DeliveryLocation | null>>;
  setAddressInput: Dispatch<SetStateAction<string>>;
  setInputDirty: Dispatch<SetStateAction<boolean>>;
  setLookingUp: Dispatch<SetStateAction<boolean>>;
}

interface ManualZipLookup {
  display: string;
  seq: number;
  lookupSeqRef: LookupSeqRef;
  zipDebounceRef: ZipDebounceRef;
  setChosenLocation: DeliveryAddressInputSetters["setChosenLocation"];
  setLookingUp: DeliveryAddressInputSetters["setLookingUp"];
}

function startManualZipLookup({
  display,
  seq,
  lookupSeqRef,
  zipDebounceRef,
  setChosenLocation,
  setLookingUp,
}: ManualZipLookup) {
  const zipMatch = display.trim().match(/(\d{5})(?:-\d{4})?$/);
  const zip = zipMatch?.[1] ?? "";
  setChosenLocation(null);
  if (!isValidUsZip(zip)) {
    setLookingUp(false);
    return;
  }
  setLookingUp(true);
  zipDebounceRef.current = setTimeout(() => {
    resolveManualDeliveryLocation(display, zip)
      .then((resolved) => {
        if (lookupSeqRef.current === seq) setChosenLocation(resolved);
      })
      .finally(() => {
        if (lookupSeqRef.current === seq) setLookingUp(false);
      });
  }, 400);
}

type LookupSeqRef = MutableRefObject<number>;
type ZipDebounceRef = MutableRefObject<
  ReturnType<typeof setTimeout> | undefined
>;

export function useDeliveryAddressInput(
  addressBook: AddressBook,
  savedAddressOptions: SavedAddressOption[],
  setters: DeliveryAddressInputSetters,
) {
  const lookupSeqRef = useRef(0);
  const zipDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const selectSavedAddress = useSelectSavedAddress(
    lookupSeqRef,
    zipDebounceRef,
    addressBook,
    setters,
  );
  const onAddressChange = useAddressChange(
    lookupSeqRef,
    zipDebounceRef,
    addressBook,
    savedAddressOptions,
    setters,
  );
  return { selectSavedAddress, onAddressChange };
}

function useSelectSavedAddress(
  lookupSeqRef: LookupSeqRef,
  zipDebounceRef: ZipDebounceRef,
  addressBook: AddressBook,
  setters: DeliveryAddressInputSetters,
) {
  const { setChosenLocation, setAddressInput, setInputDirty, setLookingUp } =
    setters;
  return useCallback(
    (selected: DeliveryLocation) => {
      lookupSeqRef.current += 1;
      clearTimeout(zipDebounceRef.current);
      setLookingUp(false);
      addressBook.clearNotice();
      setChosenLocation(selected);
      setAddressInput(formatDeliveryLocation(selected));
      setInputDirty(false);
    },
    [
      lookupSeqRef,
      zipDebounceRef,
      addressBook,
      setLookingUp,
      setChosenLocation,
      setAddressInput,
      setInputDirty,
    ],
  );
}

function useAddressChange(
  lookupSeqRef: LookupSeqRef,
  zipDebounceRef: ZipDebounceRef,
  addressBook: AddressBook,
  savedAddressOptions: SavedAddressOption[],
  setters: DeliveryAddressInputSetters,
) {
  const { setChosenLocation, setAddressInput, setInputDirty, setLookingUp } =
    setters;
  return useCallback(
    (display: string, parsed: ParsedAddress | null) => {
      setAddressInput(display);
      setInputDirty(true);
      clearTimeout(zipDebounceRef.current);
      setLookingUp(false);
      addressBook.clearNotice();
      const seq = (lookupSeqRef.current += 1);
      if (parsed && isValidUsZip(parsed.zip)) {
        const verified = toDeliveryLocation(parsed);
        setChosenLocation(verified);
        void addressBook.save(verified, savedAddressOptions).then((savedId) => {
          if (savedId && lookupSeqRef.current === seq) {
            setChosenLocation({ ...verified, savedAddressId: savedId });
          }
        });
        return;
      }

      startManualZipLookup({
        display,
        seq,
        lookupSeqRef,
        zipDebounceRef,
        setChosenLocation,
        setLookingUp,
      });
    },
    [
      lookupSeqRef,
      zipDebounceRef,
      addressBook,
      savedAddressOptions,
      setAddressInput,
      setInputDirty,
      setLookingUp,
      setChosenLocation,
    ],
  );
}

function toDeliveryLocation(parsed: ParsedAddress): DeliveryLocation {
  return {
    zip: parsed.zip,
    city: parsed.city,
    state: parsed.state,
    line1: parsed.line1 || undefined,
    line2: parsed.line2 || undefined,
    latitude: parsed.lat,
    longitude: parsed.lng,
  };
}
